import { Component, lazy, Suspense, type ReactNode } from "react";
import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";

import { PlatformAnalytics } from "@/features/analytics/PlatformAnalytics";
import { MaintenanceGate } from "@/features/maintenance/MaintenanceGate";
import type { QueryClient } from "@tanstack/react-query";

/**
 * Router context — carried through every route's ``loader`` / ``beforeLoad``.
 * The shared TanStack Query client lets route loaders warm data via
 * ``ensureQueryData`` (prefetched on hover/focus by ``defaultPreload:
 * "intent"``) so the destination paints from cache instead of spinning.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Root route — minimal shell.
 *
 * Lingui's `<I18nProvider>` already wraps `<RouterProvider>` in `main.tsx`
 * (one provider per app, mounted at the React root), so we do not re-wrap
 * here. The root simply owns the `<Outlet />` and, in development only, the
 * router devtools overlay.
 *
 * Devtools are tree-shaken in production via `import.meta.env.DEV` + lazy
 * import — the `@tanstack/react-router-devtools` chunk never enters the prod
 * bundle.
 *
 * Devtools are gated behind ``VITE_TSR_DEVTOOLS=1`` in addition to ``DEV``
 * (June 1, 2026). In our local pnpm tree the lockfile resolves
 * ``@tanstack/react-router-devtools`` to 1.167.0 while the router itself is
 * on 1.170.4 — two separate context module instances → ``useRouterState``
 * returns ``null`` and the devtools subtree crashes with
 * ``Cannot read properties of null (reading 'isServer')`` on every page
 * load. The ErrorBoundary keeps the page interactive even if someone
 * re-enables the flag without realigning versions.
 */

const TanStackRouterDevtools =
  import.meta.env.DEV && import.meta.env.VITE_TSR_DEVTOOLS === "1"
    ? lazy(() =>
        import("@tanstack/react-router-devtools").then((m) => ({
          default: m.TanStackRouterDevtools,
        })),
      )
    : null;

/** Swallow the devtools' "Cannot read properties of null" version-mismatch
 *  crash so the actual app never inherits the error boundary. */
class DevtoolsErrorBoundary extends Component<
  { readonly children: ReactNode },
  { readonly hasError: boolean }
> {
  override state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  override componentDidCatch(error: unknown) {
    if (import.meta.env.DEV) {
      console.warn("[TanStackRouterDevtools] disabled after error:", error);
    }
  }
  override render() {
    return this.state.hasError ? null : this.props.children;
  }
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  return (
    <MaintenanceGate>
      <Outlet />
      <PlatformAnalytics />
      {TanStackRouterDevtools ? (
        <DevtoolsErrorBoundary>
          <Suspense fallback={null}>
            <TanStackRouterDevtools position="bottom-right" />
          </Suspense>
        </DevtoolsErrorBoundary>
      ) : null}
    </MaintenanceGate>
  );
}
